/**
 * AnimatedDrawingRenderer - Animated rendering of drawing paths
 *
 * Handles animation with two-layer rendering:
 * - Background: Full route dimmed
 * - Foreground: Traveled portion highlighted
 *
 * Supports path split modifiers for pre-snap motion/shift.
 * Pure visual component - no state or handlers.
 */

import { useMemo } from 'react'
import type { Drawing } from '@/types/Drawing.types'
import type { BlockModifier, PathSplitModifier } from '@/components/blocks/modifiers/Types'
import { useBlockRenderContext } from '@/components/blocks/BlockRenderContext'
import { PreSnapIndicator } from '@/components/blocks/modifiers/PreSnapIndicator'
import { buildSVGPath } from '@/utils/SvgPath.utils'
import { useTheme } from '@/contexts/SettingsContext'
import { getThemeAwareColor } from '@/utils/ColorUtils'
import { FilledLineEnding } from '@/components/canvas/FilledLineEnding'
import {
	DASH_PATTERN_LENGTH_MULTIPLIER,
	DASH_PATTERN_GAP_MULTIPLIER,
	LINE_END_NONE,
} from '@/constants'

interface AnimatedDrawingRendererProps {
	drawing: Drawing
	modifiers: BlockModifier[]
	animationTime: number // 0-1 progress
}

/**
 * Renders animated drawing paths with two-layer rendering
 *
 * @param drawing - The resolved drawing data
 * @param modifiers - Block modifiers (pre-snap markers, etc.)
 * @param animationTime - Animation progress (0-1)
 *
 * Uses two-layer rendering:
 * - Background layer: Full route dimmed (opacity 0.3)
 * - Foreground layer: Traveled portion using strokeDasharray animation
 *
 * For path split modifiers, renders pre-snap indicator dimmed.
 */
export function AnimatedDrawingRenderer({
	drawing,
	modifiers,
	animationTime,
}: AnimatedDrawingRendererProps) {
	const { theme } = useTheme()
	const { scale, coordinateTransform } = useBlockRenderContext()

	// Build SVG path with length calculation and coordinate transform (Y-flip)
	const { d, pathLength, endDirection } = useMemo(() => {
		const result = buildSVGPath(drawing, coordinateTransform)
		// Calculate path length for animation
		const pathElement = document.createElementNS(
			'http://www.w3.org/2000/svg',
			'path'
		)
		pathElement.setAttribute('d', result.d)
		const length = pathElement.getTotalLength()

		return { ...result, pathLength: length }
	}, [drawing, coordinateTransform])

	if (!d) return null

	// Check for path split modifier (pre-snap)
	const pathSplitMod = modifiers.find((m) => m.type === 'path_split') as PathSplitModifier | undefined

	// Extract style properties
	const { color, strokeWidth, lineStyle, lineEnd } = drawing.style
	const displayColor = getThemeAwareColor(color, theme)

	// Apply scale factors
	const scaledStrokeWidth = strokeWidth * (scale?.stroke ?? 1.0)
	const finalStrokeWidth = Math.max(scaledStrokeWidth, scale?.minStroke ?? 1)

	// Calculate dash pattern for background paths if dashed
	const dashArray =
		lineStyle === 'dashed'
			? [
					finalStrokeWidth * DASH_PATTERN_LENGTH_MULTIPLIER,
					finalStrokeWidth * DASH_PATTERN_GAP_MULTIPLIER,
			  ].join(' ')
			: undefined

	// Calculate dash offset for animation
	const strokeDashoffset = pathLength * (1 - animationTime)

	// If path split modifier exists, render with pre-snap indicator (dimmed)
	if (pathSplitMod) {
		return (
			<g>
				{/* Background: full route dimmed */}
				<path
					d={d}
					stroke={displayColor}
					strokeWidth={finalStrokeWidth}
					strokeDasharray={dashArray}
					opacity={0.3}
					strokeLinecap="round"
					strokeLinejoin="round"
					fill="none"
				/>

				{/* Foreground: traveled portion */}
				<path
					d={d}
					stroke="var(--progress-traveled)"
					strokeWidth={finalStrokeWidth}
					strokeDasharray={pathLength}
					strokeDashoffset={strokeDashoffset}
					strokeLinecap="round"
					strokeLinejoin="round"
					fill="none"
				/>

				{/* Pre-snap indicator (dimmed) */}
				<PreSnapIndicator
					position={pathSplitMod.indicator.position}
					motionType={pathSplitMod.indicator.motionType}
					opacity={0.3}
				/>
			</g>
		)
	}

	const hasEnding = lineEnd !== LINE_END_NONE && endDirection

	return (
		<g style={{ pointerEvents: 'none' }}>
			{/* Background: route + ending composited at group opacity
			    to prevent overlap artifacts at the junction */}
			<g opacity={0.3}>
				<path
					d={d}
					stroke={displayColor}
					strokeWidth={finalStrokeWidth}
					strokeDasharray={dashArray}
					strokeLinecap="round"
					strokeLinejoin="round"
					fill="none"
				/>
				{hasEnding && (
					<FilledLineEnding
						lineEnd={lineEnd as 'arrow' | 'tShape'}
						point={endDirection!.point}
						angle={endDirection!.angle}
						strokeWidth={finalStrokeWidth}
						color={displayColor}
					/>
				)}
			</g>

			{/* Foreground: traveled portion */}
			<path
				d={d}
				stroke="var(--progress-traveled)"
				strokeWidth={finalStrokeWidth}
				strokeDasharray={pathLength}
				strokeDashoffset={strokeDashoffset}
				strokeLinecap="round"
				strokeLinejoin="round"
				fill="none"
			/>
		</g>
	)
}
